/**
 * Clears or sanitizes the DataTable state from localStorage before PrimeVue restores it.
 *
 * Two problems are handled:
 *
 * 1. Corrupt sort metadata. In `sort-mode="multiple"` a persisted single-sort `sortField`
 *    must never be set: it makes PrimeVue's `sorted` getter true while `multiSortMeta` is
 *    empty, so `sortMultiple` reads `d_multiSortMeta[0].field` on an empty array and throws,
 *    unmounting the whole app. Invalid `multiSortMeta` entries are treated the same way.
 *
 * 2. Stale persisted `filters`. Per-column filters are owned by `useColumnFilters`, which
 *    builds a `filters` object with a `global` entry plus one entry per filterable column.
 *    State persisted before those columns gained filters only holds `{ global }`; restoring
 *    it drops the per-column keys, so opening a column filter menu reads `.operator` on an
 *    undefined entry and throws. The persisted `filters` are therefore stripped so PrimeVue
 *    falls back to the composable-provided filters (filter values simply don't persist).
 */
export function clearCorruptDataTableState(stateKey: string): void {
  const stored = localStorage.getItem(stateKey);
  if (!stored) return;

  try {
    const parsed = JSON.parse(stored);
    const hasInvalidSort = parsed?.multiSortMeta?.some(
      (m: unknown) => !m || typeof (m as { field?: unknown }).field !== 'string'
    );
    const hasStaleSingleSort = typeof parsed?.sortField === 'string';
    if (hasInvalidSort || hasStaleSingleSort) {
      localStorage.removeItem(stateKey);
      return;
    }
    if (parsed && typeof parsed === 'object' && 'filters' in parsed) {
      delete parsed.filters;
      localStorage.setItem(stateKey, JSON.stringify(parsed));
    }
  } catch {
    localStorage.removeItem(stateKey);
  }
}

